import { __awaiter } from '../node_modules/tslib/tslib.es6.mjs.js';
import { localStorageProxy } from '@breville/xps-utils/dist/localStorageProxy/localStorageProxy';
import algoliasearch from 'algoliasearch';

/**
 * ALGOLIA PROXY
 *
 * Utility to communicate with custom Algolia Proxy endpoint
 * Creates a unique URL based on all variables to then have the ability to cache at the cdn level
 * @see https://breville.atlassian.net/wiki/spaces/EA/pages/4560257140/Algolia+Proxy
 */
// ! UPDATE THIS LIST WHEN ADDING NEW METHODS, THIS WILL ALSO REQUIRE BE PROXY TO UPDATE THE WHITELIST
const AVAILABLE_METHODS = ['search', 'getObjects'];
function algoliaProxy({ apiKey, appId, endpoint, indexKey }) {
    const nativeClient = nativeClientBackdoor({ apiKey, appId, indexKey });
    if (nativeClient)
        return nativeClient;
    const searchIndexProxy = AVAILABLE_METHODS.reduce((client, methodName) => {
        client[methodName] = (...args) => {
            const proxyUrl = createAlgoliaProxyUrl({ apiKey, appId, endpoint, indexKey, methodName, methodArgs: args });
            return fetch(proxyUrl).then((res) => res.json());
        };
        return client;
    }, {});
    searchIndexProxy.useNativeClient = () => getNativeAlgoliaClient({ apiKey, appId, indexKey });
    function searchClientDisableMultiQueriesSearch(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            // * break apart the multi index query to avoid request header limit issues
            const [queries, options] = args;
            const output = yield Promise.all(queries.map((query) => searchIndexProxy.search([query], options)));
            // aggregate the results
            return output.reduce((final, item) => {
                final.results.push(...item.results);
                return final;
            }, { results: [] });
        });
    }
    // if no 'indexKey' provided then we pass back a client with 'initIndex' method
    if (!indexKey) {
        return {
            initIndex: (indexKey) => algoliaProxy({ apiKey, appId, endpoint, indexKey }),
            // borrow the 'search' method from the searchIndexProxy as we know at this point 'indexKey' would be unavailable upon creation, this means the searchClient would be utilized
            search: searchIndexProxy['search'], // * searchClient().search() used for plp pages
            useNativeClient: () => getNativeAlgoliaClient({ apiKey, appId }),
            // * option to break apart the multi index query to avoid request header limit issues (used for SBC PLP)
            searchClientWithMultiQueriesDisabled: {
                search: searchClientDisableMultiQueriesSearch
            }
        };
    }
    return searchIndexProxy;
}
function createAlgoliaProxyUrl(args) {
    const { appId, apiKey, indexKey, methodName, methodArgs } = args;
    let { endpoint } = args;
    try {
        if (!endpoint || !appId || !apiKey || !methodName || !methodArgs.length)
            throw new Error('Missing required parameters for fetchAlgoliaProxy');
        // strip 'https://' from the url if it exists
        try {
            const _url = new URL(endpoint);
            endpoint = _url.hostname;
        }
        catch (e) {
            // do nothing
        }
        // * convert any object such as 'requestOptions' to a sorted array of {key, value}[] to ensure we control the order of the keys for caching purposes
        const normalizedMethodArgs = methodArgs.map((arg) => {
            if (typeof arg === 'object' && !Array.isArray(arg) && arg !== null) {
                return Object.entries(arg)
                    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, 'en-US'))
                    .map(([key, value]) => ({ key, value }));
            }
            return arg;
        });
        // this ensures we create a unique url to be cached
        const payload = encodeURIComponent(JSON.stringify({
            methodName,
            methodArgs: normalizedMethodArgs
        }));
        // construct the url
        // eg: https://api.foodthinkers.com/algoliaProxy?appId=S83ELHQBRF&apiKey=9fa6aa2f14b022b0c829f3425aa9a666&indexKey=Breville_BUS&payload=%7B%22methodName%22%3A%22search%22%2C%22methodArgs%22%3A%5B%22%22%2C%7B%22facetFilters%22%3A%5B%22hasPromotion%3Atrue%22%5D%2C%22offset%22%3A0%2C%22length%22%3A1000%7D%5D%7D
        const indexKeyPath = indexKey ? `&indexKey=${indexKey}` : '';
        return `https://${endpoint}/algoliaProxy?appId=${appId}&apiKey=${apiKey}${indexKeyPath}&payload=${payload}`;
    }
    catch (error) {
        console.error('createAlgoliaProxyUrl', error);
        return '';
    }
}
// * option to bypass the proxy if needed via localstorage -> 'algoliaProxy = false' (eg: for validation purposes)
function nativeClientBackdoor({ apiKey, appId, indexKey }) {
    const isLocalStorageAvailable = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    if (isLocalStorageAvailable) {
        const isAlgoliaProxyDisabled = localStorageProxy.getItem('algoliaProxy') === 'false';
        if (isAlgoliaProxyDisabled) {
            console.log('algoliaProxy: DISABLED via localStorage');
            return getNativeAlgoliaClient({ apiKey, appId, indexKey });
        }
    }
}
function getNativeAlgoliaClient({ apiKey, appId, indexKey }) {
    const nativeClient = algoliasearch(appId, apiKey);
    return (indexKey ? nativeClient.initIndex(indexKey) : nativeClient);
}

export { algoliaProxy, createAlgoliaProxyUrl, nativeClientBackdoor };
//# sourceMappingURL=algolia-proxy.js.map
